import moment from 'moment'
import React from 'react'
import { connect } from 'react-redux'
import _ from 'underscore'
import { TidewaysContext } from '../app/context'
import { Entity } from '../app/entity'
import Navigator from '../app/navigator'
import store from '../app/store'
import { BackendError } from '../domain/BackendError'
import { ErrorContext, ErrorGroupIncident, errorGroupIsNonCritical, ErrorGroupObservation, IssueType } from '../domain/Error'
import BoxBody from '../helper/boxBody'
import CodeCover from '../helper/code'
import LargeNumber from '../helper/large-number'
import Link from '../helper/link'
import Loading from '../helper/loading'
import Milliseconds from '../helper/milliseconds'
import MomentDate from '../momentDate'
import { ApplicationState } from '../redux/ApplicationState'
import { Alert } from '../ui/alert'
import { Box, BoxHeader, BoxTitle } from '../ui/box'
import { Context as DetailContext } from './details/context'
import { ErrorHistogram } from './details/ErrorHistogram'
import { IssueWorkflowButtonGroup } from './details/IssueWorkflowButtonGroup'
import StackTrace from './details/stackTrace'
import StatusLabel from './details/statusLabel'
import IncidentDetails from './IncidentDetails'
import { ObservationDetails } from './ObservationDetails'
import NotificationLog from './details/NotificationLog'
import { EventGraphData, TidewaysEvent } from '../domain/Event'

interface Props {
    navigator: any
    router: any
    parameters: {
        offset: string
        page: string
        error: string
        reload: string
    }
    loading: boolean
    data: ErrorContext | null
    tidewaysContext: TidewaysContext
    loadingError: BackendError | null
    events?: TidewaysEvent[]
}

export class DetailsComponent extends React.Component<Props> {
    public static defaultProps = {
        loading: false,
        data: null,
    }

    public updateErrorState = href => {
        jQuery.ajax({
            url: href,
            success: data => {
                store.dispatch({ type: 'Errors.purge' })

                // we need to force a reload, otherwise no new ajax call is made.
                const parameters = _.extend({}, this.props.parameters)
                parameters.reload = +(parameters.reload || 0) + 1
                this.props.navigator.replace(parameters)
            },
        })
    }

    public render() {
        if (this.props.loading) {
            return <Loading {...this.props} height={100} title="Error" />
        }
        if (this.props.loadingError?.status === 404) {
            return (
                <Box>
                    <BoxHeader>
                        <BoxTitle>Not found</BoxTitle>
                    </BoxHeader>
                    <BoxBody>
                        <h4>
                            <i className="fa fa-light fa-exclamation-circle tw-mr-4"></i>
                            This error doesn&apos;t exist anymore.
                        </h4>
                    </BoxBody>
                </Box>
            )
        }
        if (!this.props.data) {
            return (
                <Box>
                    <BoxHeader>
                        <BoxTitle>Error</BoxTitle>
                    </BoxHeader>
                    <BoxBody>
                        <h4>
                            <i className="fa-light fa-exclamation-circle tw-mr-4"></i>
                            This error could not be loaded.
                        </h4>
                    </BoxBody>
                </Box>
            )
        }

        const { route } = this.props.tidewaysContext.route
        const { errorGroup, snapshotComparison, operations } = this.props.data
        const relatedAlertActionExecutions = this.props.data.errorGroup.relatedAlertActionExecutions
        const occurances = this.props.data.occurances && this.props.data.occurances.occurances
        const error = this.props.data.error || { occurances: 1, time: errorGroup.lastOccured.date }
        const message = error.message || errorGroup.lastMessage
        const errorCode = error.code || '0'
        const stackTrace = error.stackTrace || errorGroup.lastStackTrace
        const annotations = error.annotations || errorGroup.annotations
        const showContext = Object.keys(annotations).length !== 0
        const offset = this.props.parameters.offset ? parseInt(this.props.parameters.offset, 10) : 0
        const commonParams = {
            error: errorGroup.id,
            offset,
            page: this.props.parameters.page || 1,
        }

        if (errorGroup.level === IssueType.OBSERVATION) {
            return (
                <ObservationDetails
                    errorGroup={errorGroup as ErrorGroupObservation}
                    navigator={this.props.navigator}
                    parameters={this.props.parameters}
                    router={this.props.router}
                    bottleneckBeta={this.props.tidewaysContext.application.license.bottleneckBeta}
                />
            )
        }

        if (errorGroup.level === IssueType.INCIDENT) {
            return (
                <IncidentDetails
                    errorGroup={errorGroup as ErrorGroupIncident}
                    loading={this.props.loading}
                    navigator={this.props.navigator}
                    parameters={this.props.parameters}
                    tidewaysContext={this.props.tidewaysContext}
                    snapshotComparison={snapshotComparison}
                    relatedAlertActionExecutions={relatedAlertActionExecutions}
                    events={this.props.events}
                />
            )
        }

        const title = errorGroupIsNonCritical(errorGroup) ? errorGroup.originatingFunction : errorGroup.type

        return (
            <div>
                <div className="box error-item-detail">
                    <div className="box-header">
                        <h3 className="box-title">{title}</h3>
                        <div className="box-tools pull-right">
                            {!errorGroup.notify ? (
                                <div
                                    className="inline-block tw-tooltip tw-tooltip-dataText tw-tooltip--bottomLeft"
                                    data-tooltip-text="Notifications muted"
                                >
                                    <i className="fa fa-microphone-slash tw-mr-8" />
                                </div>
                            ) : null}
                            <StatusLabel status={errorGroup.status} />
                        </div>
                    </div>

                    <div className="box-header">
                        <h3 style={{ paddingBottom: '0px' }} className="box-title">
                            <strong>#{offset + 1}</strong>&nbsp;
                            <span>
                                {moment
                                    .utc(error.time, 'YYYY-MM-DD HH:mm:ss')
                                    .local()
                                    .format('MMMM Do YYYY, HH:mm')}
                            </span>
                            {error.occurances <= 1 ? null : <span className="text-muted">&nbsp;({error.occurances}x in this minute)</span>}
                        </h3>

                        <div className="box-tools pull-right">
                            <div className="btn-group">
                                {offset >= 1 ? (
                                    <>
                                        <Link
                                            className="btn btn-default"
                                            route={route}
                                            params={{
                                                ...commonParams,
                                                offset: 0,
                                            }}
                                        >
                                            <i className="fa fa-light fa-angle-double-left" /> Newest
                                        </Link>
                                        <Link
                                            className="btn btn-default"
                                            route={route}
                                            params={{
                                                ...commonParams,
                                                offset: offset - 1,
                                            }}
                                        >
                                            <i className="fa fa-light fa-angle-left" />
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <button className="btn btn-default" disabled>
                                            <i className="fa fa-light fa-angle-double-left" /> Newest
                                        </button>
                                        <button className="btn btn-default" disabled>
                                            <i className="fa fa-light fa-angle-left" />
                                        </button>
                                    </>
                                )}
                                {offset < this.props.data.totalOccurances - 1 ? (
                                    <>
                                        <Link
                                            className="btn btn-default"
                                            route={route}
                                            params={{
                                                ...commonParams,
                                                offset: offset + 1,
                                            }}
                                        >
                                            <i className="fa fa-light fa-angle-right" />
                                        </Link>
                                        <Link
                                            className="btn btn-default"
                                            route={route}
                                            params={{
                                                ...commonParams,
                                                offset: this.props.data.totalOccurances - 1,
                                            }}
                                        >
                                            Oldest <i className="fa fa-light fa-angle-double-right" />
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <button className="btn btn-default" disabled>
                                            <i className="fa fa-light fa-angle-right" />
                                        </button>
                                        <button className="btn btn-default" disabled>
                                            Oldest <i className="fa fa-light fa-angle-double-right" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <BoxBody {...this.props} className="no-padding">
                        <div style={{ padding: '10px' }}>
                            <div className="row">
                                <div className="col-md-12">
                                    {message ? (
                                        error.level === IssueType.SLOWSQL ? (
                                            <>
                                                {'duration' in error.annotations ? (
                                                    <BoxTitle className="tw-font-size-20">
                                                        Query Time:&nbsp;
                                                        <Milliseconds value={parseInt(error.annotations.duration, 10)} />
                                                    </BoxTitle>
                                                ) : null}
                                                <CodeCover type="sql" code={message} />
                                            </>
                                        ) : (
                                            <blockquote>
                                                <p>
                                                    {message
                                                        .trim()
                                                        .split(/(?:\r\n|\r|\n)+/)
                                                        .map((line, index) => {
                                                            return (
                                                                <span key={index}>
                                                                    {line}
                                                                    <br />
                                                                </span>
                                                            )
                                                        })}
                                                </p>
                                            </blockquote>
                                        )
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-7">
                                    <dl className="dl-horizontal">
                                        {errorCode.length > 0 && errorCode !== '0' ? (
                                            <>
                                                <dt>Code</dt>
                                                <dd>{errorCode}</dd>
                                            </>
                                        ) : null}

                                        <dt>Source</dt>
                                        <dd>{errorGroup.source}</dd>

                                        <dt>Occurrences</dt>
                                        <dd>
                                            <LargeNumber class="" useUnitClass={false} value={errorGroup.occurances} />
                                        </dd>

                                        {errorGroup.occurancesSinceLastRelease < errorGroup.occurances ? <dt>Since Last Release</dt> : ''}

                                        {errorGroup.occurancesSinceLastRelease < errorGroup.occurances ? (
                                            <dd>
                                                <LargeNumber class="" useUnitClass={false} value={errorGroup.occurancesSinceLastRelease} />
                                            </dd>
                                        ) : (
                                            ''
                                        )}

                                        <dt>First Occurrence</dt>
                                        <dd>
                                            <MomentDate date={errorGroup.firstOccured.date} />
                                        </dd>

                                        <dt>Last Occurrence</dt>
                                        <dd>
                                            <MomentDate date={errorGroup.lastOccured.date} />
                                        </dd>

                                        <dt>Last (Re-)Opened</dt>
                                        <dd>
                                            <MomentDate date={errorGroup.lastOpened.date} />
                                        </dd>

                                        {errorGroup.environments && errorGroup.environments.length > 0 ? (
                                            <div>
                                                <dt>Environment</dt>
                                                <dd>{errorGroup.environments.join(', ')}</dd>
                                            </div>
                                        ) : (
                                            ''
                                        )}

                                        {errorGroup.services && errorGroup.services.length > 0 ? (
                                            <div>
                                                <dt>Services</dt>
                                                <dd>{errorGroup.services.join(', ')}</dd>
                                            </div>
                                        ) : (
                                            ''
                                        )}
                                    </dl>
                                </div>

                                <div className="col-md-5 text-right">
                                    <IssueWorkflowButtonGroup
                                        errorGroup={errorGroup}
                                        updateErrorState={this.updateErrorState}
                                        loading={this.props.loading}
                                        navigator={this.props.navigator}
                                    />
                                </div>

                                {errorGroup.level === IssueType.ERROR || errorGroup.level === IssueType.SLOWSQL ? (
                                    <div className="col-md-12">
                                        <dl className="dl-horizontal">
                                            <dt>Transactions</dt>
                                            <dd>
                                                <ul
                                                    style={{
                                                        paddingLeft: '0px',
                                                        listStyleType: 'none',
                                                    }}
                                                >
                                                    {_.map(operations, operation => (
                                                        <li key={operation.name}>
                                                            {operation.name} ({operation.count})
                                                        </li>
                                                    ))}
                                                </ul>
                                            </dd>
                                        </dl>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {errorGroup.level === IssueType.ERROR || errorGroup.level === IssueType.SLOWSQL ? (
                            <ErrorHistogram occurrences={occurances} errorGroupId={errorGroup.id} />
                        ) : null}
                    </BoxBody>
                </div>

                <ul className="nav nav-pills nav-pills-tideways">
                    <li className="active">
                        <a href="#stacktrace" data-target="#stacktrace" data-toggle="tab">
                            Stacktrace
                        </a>
                    </li>
                    {showContext ? (
                        <li>
                            <a href="#context" data-target="#context" data-toggle="tab">
                                Context
                            </a>
                        </li>
                    ) : null}
                    <li>
                        <a href="#notificationLog" data-target="#notificationLog" data-toggle="tab">
                            Notification Log
                        </a>
                    </li>
                </ul>

                <Box>
                    <BoxBody noPadding className="tw-min-height-48">
                        <div className="tab-content no-padding">
                            <div className="tab-pane active" id="stacktrace">
                                {stackTrace && errorGroup.level === IssueType.DEPRECATED ? (
                                    <div className="tw-m-8">
                                        <Alert level="info">
                                            <p>
                                                Deprecations are grouped by their message only. This stack trace shows the last occurrence
                                                of this deprecation, but there could be other locations in the application where it gets
                                                triggered.
                                            </p>
                                        </Alert>
                                    </div>
                                ) : null}

                                {(stackTrace && errorGroup.level === IssueType.NOTICE) || errorGroup.level === IssueType.WARNING ? (
                                    <div className="tw-m-8">
                                        <Alert level="info">
                                            <p>
                                                Notices and Warnings are grouped by their file and line number only. This stack trace shows
                                                the last occurrence, but there could be other locations in the application where it gets
                                                triggered.
                                            </p>
                                        </Alert>
                                    </div>
                                ) : null}

                                {stackTrace && errorGroup ? (
                                    <StackTrace
                                        trace={stackTrace}
                                        errorMessage={error ? error.message : errorGroup.lastMessage}
                                        errorType={error ? error.type : errorGroup.type}
                                    />
                                ) : (
                                    <p className="tw-m-8">There is no stacktrace available for this exception.</p>
                                )}
                            </div>

                            {showContext ? (
                                <div className="tab-pane" id="context">
                                    <DetailContext annotations={annotations} />
                                </div>
                            ) : null}
                            <div className="tab-pane" id="notificationLog">
                                <NotificationLog actionExecutions={relatedAlertActionExecutions}></NotificationLog>
                            </div>
                        </div>
                    </BoxBody>
                </Box>
            </div>
        )
    }
}

export default connect((globalState: ApplicationState) => {
    const error = globalState.errors.errors[globalState.tidewaysContext.route.parameters.error] || new Entity()
    const routeParameters = globalState.tidewaysContext.route.parameters

    // Depending on which route we are using the currently selected event id might be given as "event" or "error"
    const key = routeParameters.event || routeParameters.error

    const eventGraph = globalState.events?.eventGraphs?.[key] || (new Entity() as Entity<EventGraphData>)

    return {
        tidewaysContext: globalState.tidewaysContext,
        router: globalState.tidewaysContext.router,
        navigator: new Navigator(globalState.tidewaysContext.route),
        parameters: globalState.tidewaysContext.route.parameters,
        data: error.data,
        loading: error.loading,
        loadingError: error.error,
        events: eventGraph.data?.events,
    }
})(DetailsComponent)
